/* JOBY Cross-Sell — global behaviour
 *
 * Loaded once from theme.liquid with `defer`. Uses a single delegated click
 * listener on `document`, so it keeps working no matter how many times the
 * theme re-renders the cart drawer through the Section Rendering API.
 *
 * The actual discount is a native "Buy X Get Y" automatic discount set up in
 * admin. This script only gets the right items into the cart and refreshes
 * the drawer; Shopify applies the price reduction itself.
 */
(function () {
  'use strict';

  function getCartDrawer() {
    return document.getElementById('CartDrawer');
  }

  // Resolve the MAIN product's variant. We look only inside the widget's own
  // product section (so we never pick up a recommended/complementary product's
  // add form elsewhere on the page), prefer the customer's live selection, and
  // fall back to the server-rendered variant. We also refuse any value that
  // equals the add-on, as a safety net.
  function getMainVariant(scope) {
    var addonId = Number(scope.getAttribute('data-addon-variant'));
    var fallback = Number(scope.getAttribute('data-main-variant')) || null;

    var section = scope.closest('.shopify-section') || document;
    // This theme's product form posts to /cart, so match /cart broadly (scoped
    // to the section), and prefer the dedicated product-form element.
    var input = section.querySelector(
      'product-form [name="id"], form[action$="/cart"] [name="id"], form[action*="/cart/add"] [name="id"]'
    );
    var live = input ? Number(input.value) : NaN;

    if (live && live !== addonId) return live;
    if (fallback && fallback !== addonId) return fallback;
    return null;
  }

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    });
  }

  // Re-render the cart drawer in place. We replace only the inner regions that
  // change (items list, footer, count) and leave the <cart-drawer> wrapper
  // untouched, so an already-open drawer stays open and its animations aren't
  // reset. The custom elements inside re-upgrade automatically on insertion.
  function refreshDrawer() {
    var drawer = getCartDrawer();
    if (!drawer) return Promise.resolve(); // cart page, or drawer cart disabled
    var sectionId = drawer.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve();

    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // Cart count
        var liveCount = drawer.querySelector('cart-count');
        var newCount = doc.querySelector('cart-count');
        if (liveCount && newCount) {
          liveCount.textContent = newCount.textContent;
          liveCount.hidden = false;
        }

        // Items + everything in the scrollable region (free-shipping bar,
        // our cross-sell nudge, recommendations)
        var liveItems = drawer.querySelector('cart-items');
        var liveScroll = liveItems ? liveItems.closest('.drawer__scrollable') : null;
        var newItems = doc.querySelector('cart-items');
        var newScroll = newItems ? newItems.closest('.drawer__scrollable') : null;
        if (liveScroll && newScroll) {
          liveScroll.innerHTML = newScroll.innerHTML;
          liveScroll.classList.remove('hidden');
        }

        // Hide the empty-cart state if it's showing
        var emptyState = drawer.querySelector('.drawer__empty');
        if (emptyState) {
          var emptyScroll = emptyState.closest('.drawer__scrollable');
          if (emptyScroll && emptyScroll !== liveScroll) emptyScroll.classList.add('hidden');
        }

        // Footer (checkout button, totals)
        var liveFooter = drawer.querySelector('.drawer__footer');
        var newFooter = doc.querySelector('.drawer__footer');
        if (liveFooter && newFooter) {
          liveFooter.innerHTML = newFooter.innerHTML;
          liveFooter.classList.remove('hidden');
        }
      })
      .catch(function () { /* refresh is best-effort; the add already succeeded */ });
  }

  // Open the drawer by triggering a header opener (the theme handles the
  // animation). We avoid elements inside the drawer itself (overlay, close).
  function openDrawer() {
    var drawer = getCartDrawer();
    if (!drawer) return;
    var openers = document.querySelectorAll('[aria-controls="CartDrawer"]');
    for (var i = 0; i < openers.length; i++) {
      if (!openers[i].closest('#CartDrawer')) { openers[i].click(); return; }
    }
  }

  function handleProductAdd(scope) {
    var addon = Number(scope.getAttribute('data-addon-variant'));
    var main = getMainVariant(scope);
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = cart.items.map(function (i) { return i.variant_id; });
        var items = [];
        if (main && inCart.indexOf(main) === -1) items.push({ id: main, quantity: 1 });
        if (addon && inCart.indexOf(addon) === -1) items.push({ id: addon, quantity: 1 });
        return items.length ? addItems(items) : null;
      })
      .then(refreshDrawer)
      .then(openDrawer);
  }

  function handleCartAdd(scope) {
    var addon = Number(scope.getAttribute('data-addon-variant'));
    if (!addon) return Promise.resolve();
    return addItems([{ id: addon, quantity: 1 }]).then(refreshDrawer);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-joby-add]') : null;
    if (!btn || btn.dataset.jobyBusy) return;

    var productScope = btn.closest('[data-joby-cross-sell]');
    var cartScope = btn.closest('[data-joby-cross-sell-cart]');
    if (!productScope && !cartScope) return;

    e.preventDefault();
    btn.dataset.jobyBusy = '1';
    btn.disabled = true;
    var label = btn.querySelector('[data-joby-label]');
    var original = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';

    var run = productScope ? handleProductAdd(productScope) : handleCartAdd(cartScope);

    run.then(function () {
      if (productScope) {
        // The product widget isn't inside the drawer, so reset it for re-use.
        btn.classList.add('is-added');
        if (label) label.textContent = 'Added to cart';
        setTimeout(function () {
          btn.classList.remove('is-added');
          btn.disabled = false;
          if (label) label.textContent = original;
          delete btn.dataset.jobyBusy;
        }, 2500);
      }
      // The cart nudge gets replaced by the drawer refresh, so nothing to reset.
    }).catch(function () {
      if (label) label.textContent = original;
      btn.disabled = false;
      delete btn.dataset.jobyBusy;
    });
  });
})();
